<template>
  <ValidationObserver
    ref="customFieldObserver"
    v-slot="{ valid, pristine }" >
    <b-modal
      id="whatsapp-display-name-change-modal"
      ref="whatsappDisplayNameChangeModal"
      :modal-class="['settings-modal', {'change-pending': changePending}]"
      size="sm"
      no-close-on-backdrop
      centered
      hide-header-close
      @hidden="resetFields"
      @shown="focus" >
      <template #modal-header>
        <h5>Request WhatsApp display name change</h5>
      </template>

      <template v-if="changePending">
        <div class="flex column center">
          <span class="success-icon" />
          <h5>Your new display name was successfully submitted for a WhatsApp review</h5>

          <p>Once WhatsApp reviewed your request, you will receive a notification letting you know if your display name has been approved or not.</p>
          <p>Visit
            <a
              href="https://www.facebook.com/business/help/378834799515077"
              target="_blank">WhatsApp display name guidelines</a>
            for more information.
          </p>
        </div>
      </template>

      <div v-else>
        <form @submit.prevent="changeDisplayName">
          <ValidationProvider
            v-slot="{ errors }"
            name="name"
            :rules="{
              required: true,
              noDuplicate: {
                list: [currentDisplayName],
                caseSensitive: false,
                message: 'You already use this name.'
              }
            }" >
            <b-form-group>
              <label
                for="name"
                class="flex flex-align-center" >
                New display name
              </label>
              <b-input
                ref="nameInputRef"
                v-model="name"
                autocomplete="off"
                class="sb2-bordered sb2-hoverable sb2-focusable" />
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>

            </b-form-group>
          </ValidationProvider>
        </form>
        <div
          v-if="requestError"
          class="error flex flex-align-center">
          <SBIcon
            class="mr-2"
            :icon="SBIconList.ERROR"
            :color="SBColors.redPrimary"
            :size="12" />
          <span>Something went wrong. Please try again later.</span>
        </div>
        <InfoBox
          class="top-margin-20" >
          <p slot="content">
            Display names should have a relationship with your business and use branding consistent with external sources (e.g. your website).
            <a
              href="https://web.facebook.com/business/help/338047025165344?_rdc=1&_rdr"
              target="_blank" >See guidelines</a>.
          </p>
        </InfoBox>
      </div>

      <template
        slot="modal-footer">
        <b-button
          @click="hide">
          Close
        </b-button>
        <b-button
          v-if="!changePending"
          variant="primary"
          :disabled="saving || !valid || pristine"
          :class="{loading: saving}"
          class="flex settings__actions--save"
          @click="changeDisplayName()">
          <span>Save</span>
          <Spinner
            v-if="saving"
            :size="14"
            :thickness="2"
            :color="SBColors.white" />
        </b-button>
      </template>

    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import Spinner from '@/components/common/Spinner';
import SBColors from '@/helpers/lists/colors';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBIcon from '@/components/common/Icon';
import InfoBox from '@/components/business/widgets/InfoBox.widget';
import api from '@/helpers/api';

export default {
  name: 'WhatsAppDisplayNameChangeModal',
  components: {
    Spinner,
    SBIcon,
    InfoBox,
  },
  props: {
    business: {
      type: Object,
      required: false,
      default: () => {},
    },
    currentDisplayName: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      saving: false,
      name: '',
      changePending: false,
      requestError: false,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
    }),
  },
  watch: {},
  methods: {
    changeDisplayName() {
      this.$refs.customFieldObserver.validate()
        .then((valid) => {
          if (!valid) {
            return;
          }
          this.saving = true;
          const data = {
            name: this.name,
          };
          api('POST', `organisation/${this.currentOrganisation.id}/business/${this.business.id}/whatsapp/display-name-change-request`, data, null, 'w')
            .then((response) => {
              if (response.status === 200) {
                this.changePending = true;
                this.$emit('display-name-change', response.data);
              }
            })
            .catch(() => {
              this.requestError = true;
            })
            .finally(() => {
              this.saving = false;
            });
        })
        .catch(() => {
          // console.log('error');
        });
    },
    show() {
      this.$refs.whatsappDisplayNameChangeModal.show();
    },
    hide() {
      this.$refs.whatsappDisplayNameChangeModal.hide();
    },
    focus() {
      if (this.$refs.nameInputRef) {
        this.$refs.nameInputRef.focus();
      }
    },
    resetFields() {
      this.saving = false;
      this.changePending = false;
      this.requestError = false;
      this.name = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.top-margin-20 {
  margin-top: 20px;
}
</style>

<style lang="scss">
.settings-modal.change-pending {
  .modal-header {
    border-bottom: none !important;

    h5 {
      display: none;
    }
  }

  .modal-body {
    background-color: $white !important;
    padding: 14px 48px 60px !important;

    .success-icon {
      margin-bottom: 15px;
      width: 100%;
      height: 70px;
      background-image: url('~@/assets/img/SB-Icons/whatsapp-submission.svg');
      background-position: center!important;
      background-repeat: no-repeat !important;
      background-size: contain !important;
    }

    h5 {
      font-size: 16px;
      font-weight: 500;
    }

    p {
      font-size: 14px;
      font-weight: 400;
      color: $blackUltralight;
      margin-top: 18px;

      a {
        color: inherit;
        text-decoration: underline;

        &:hover {
          color: $blackPrimary;
        }
      }

      .pill {
        padding: 1px 7px 2px;
        border-radius: 3px;
        border: solid 1px #e5e9eb;
        background-color: #edf0f2;
        font-size: 9px;
        font-weight: 500;
        color: $blackPrimary;
      }
    }
  }
}</style>
